import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { confirmOrbitAction, reportOrbitHandoff, selectOrbitCandidate } from 'api/orbitActions';
import { EmbeddedBuilderEntity } from 'components/EmbeddedBuilder';
import { IOpenBuilderOptions, openBuilder } from 'containers/Orbit/builders';

import { useOrbitContext } from '../OrbitContext';
import { useOrbitNotice } from '../OrbitNoticeContext';
import { OrbitTurn } from '../types';
import OrbitL4DemoBridge from './demo/OrbitL4DemoBridge';
import { isOrbitL4DemoTurnId } from './demo/constants';
import { holdDemoModuleSaves } from './demo/engine';
import { orbitL4Status, setOrbitL4Status } from './inFlightStore';
import { lockedSegmentAttributes } from './lockedCriteria';
import { OrbitActionBuilder, OrbitActionCandidate, OrbitActionStep } from './types';
import useOrbitLayer4 from './useOrbitLayer4';

export interface IOrbitL4Actions {
  enabled: boolean;
  confirm: (turn: OrbitTurn) => void;
  select: (turn: OrbitTurn, candidate: OrbitActionCandidate) => void;
  dismiss: (turn: OrbitTurn) => void;
  openInBuilder: (
    turn: OrbitTurn,
    step: OrbitActionStep,
    builder: OrbitActionBuilder | null
  ) => void;
}

const noop = () => undefined;

const DISABLED: IOrbitL4Actions = {
  enabled: false,
  confirm: noop,
  select: noop,
  dismiss: noop,
  openInBuilder: noop
};

/**
 * A record the proposal targets (one the backend already created, or one being edited) opens in EDIT
 * mode on `target.id`; without one (audience) the builder CREATES, prefilled from the proposal.
 * Builders open on the review step unless the payload names another.
 */
export const builderOptionsFor = (
  step: OrbitActionStep,
  builder: OrbitActionBuilder | null
): IOpenBuilderOptions => ({
  module: step.target.entity as EmbeddedBuilderEntity,
  ...(step.target.id === undefined
    ? { mode: 'create-prefill' as const }
    : { mode: 'edit' as const, recordId: step.target.id }),
  ...(builder?.prefill ? { initialValues: builder.prefill } : {}),
  initialStep: builder?.step ?? 'review',
  ...(step.target.entity === 'audience'
    ? { lockedCustomAttributes: lockedSegmentAttributes(builder?.prefill ?? null) }
    : {})
});

const OrbitL4ActionContext = createContext<IOrbitL4Actions>(DISABLED);

export const useOrbitL4Actions = (): IOrbitL4Actions => useContext(OrbitL4ActionContext);

/**
 * Runs the Layer 4 card buttons. Every request answers with a NEW turn, which is handed to
 * `followTurn` so the panel polls it; a double click is absorbed here before it reaches the server.
 */
const OrbitL4ActionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const enabled = useOrbitLayer4();
  const { followTurn, revalidateTurns } = useOrbitContext();
  const { showNotice } = useOrbitNotice();

  const confirm = useCallback(
    async (turn: OrbitTurn) => {
      if (orbitL4Status(turn.id).busy) {
        return;
      }
      setOrbitL4Status(turn.id, { busy: true });

      const outcome = await confirmOrbitAction(turn.id);

      if (outcome.kind === 'ok') {
        followTurn(outcome.data);
        return;
      }
      if (outcome.kind === 'expired') {
        setOrbitL4Status(turn.id, { busy: false, expiredMessage: outcome.message });
        return;
      }
      setOrbitL4Status(turn.id, { busy: false, failed: true });
    },
    [followTurn]
  );

  const select = useCallback(
    async (turn: OrbitTurn, candidate: OrbitActionCandidate) => {
      if (orbitL4Status(turn.id).busy) {
        return;
      }
      setOrbitL4Status(turn.id, { busy: true });

      const outcome = await selectOrbitCandidate(turn.id, {
        step_id: candidate.step_id,
        target_id: candidate.id
      });

      if (outcome.kind === 'ok') {
        followTurn(outcome.data);
        return;
      }
      // Most often a double click or a picker a newer proposal replaced: re-read and redraw.
      if (outcome.kind === 'invalid') {
        setOrbitL4Status(turn.id, { busy: false });
        revalidateTurns();
        return;
      }
      setOrbitL4Status(turn.id, { busy: false, failed: true });
    },
    [followTurn, revalidateTurns]
  );

  const openInBuilder = useCallback(
    async (turn: OrbitTurn, step: OrbitActionStep, builder: OrbitActionBuilder | null) => {
      if (orbitL4Status(turn.id).busy) {
        return;
      }
      // The card's buttons stay disabled while the builder is open; a cancel brings them back.
      setOrbitL4Status(turn.id, { ...orbitL4Status(turn.id), busy: true, builderOpen: true });
      // Demo turns save into the demo, never into the tenant.
      const release = isOrbitL4DemoTurnId(turn.id) ? holdDemoModuleSaves() : noop;
      const saved = await openBuilder(builderOptionsFor(step, builder)).finally(release);
      if (saved === null) {
        setOrbitL4Status(turn.id, { ...orbitL4Status(turn.id), busy: false, builderOpen: false });
        return;
      }
      setOrbitL4Status(turn.id, { ...orbitL4Status(turn.id), builderOpen: false });
      const record = { entity: saved.entity, id: saved.id };
      const outcome = await reportOrbitHandoff(turn.id, {
        resource_type: saved.entity,
        resource_id: saved.id
      });
      if (outcome.kind === 'ok') {
        followTurn(outcome.data);
        return;
      }
      setOrbitL4Status(turn.id, { busy: false, savedRecord: record });
      showNotice({
        tone: 'failed',
        messageKey: outcome.kind === 'conflict' ? 'layer4.handoffConflict' : 'layer4.handoffFailed'
      });
    },
    [followTurn, showNotice]
  );

  const dismiss = useCallback((turn: OrbitTurn) => {
    setOrbitL4Status(turn.id, { ...orbitL4Status(turn.id), dismissed: true });
  }, []);

  const value = useMemo<IOrbitL4Actions>(
    () =>
      enabled
        ? {
            enabled,
            confirm: (turn: OrbitTurn) => {
              confirm(turn).catch(noop);
            },
            select: (turn: OrbitTurn, candidate: OrbitActionCandidate) => {
              select(turn, candidate).catch(noop);
            },
            dismiss,
            openInBuilder: (
              turn: OrbitTurn,
              step: OrbitActionStep,
              builder: OrbitActionBuilder | null
            ) => {
              openInBuilder(turn, step, builder).catch(noop);
            }
          }
        : DISABLED,
    [enabled, confirm, select, dismiss, openInBuilder]
  );

  return (
    <OrbitL4ActionContext.Provider value={value}>
      {children}
      <OrbitL4DemoBridge enabled={enabled} />
    </OrbitL4ActionContext.Provider>
  );
};

export default OrbitL4ActionProvider;
